import { InfoOutlined } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useMemo } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocalization } from "../hooks/useLocalization";
import { Tooltip } from "./Tooltip";

interface SeatPickerProps {
  value: number | null;
  onChange: (seats: number | null) => void;
  label?: string;
  mobileLabel?: string;
  min?: number | null;
  max?: number;
  maxLength?: number;
  checked?: boolean;
  handleSwitchChange?: (checked: boolean) => void;
  labelWidth?: number;
  showCompactView?: boolean;
  showTooltip?: boolean;
}

export const SeatPicker: FC<SeatPickerProps> = ({
  value,
  onChange,
  label,
  mobileLabel,
  min = 1,
  max = 9999,
  maxLength = 4,
  checked,
  handleSwitchChange,
  labelWidth = 60,
  showCompactView = false,
  showTooltip = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const effectiveMin = useMemo(() => {
    if (min === null) {
      return null;
    } else if (min === 0) {
      return 1;
    } else {
      return min;
    }
  }, [min]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      onChange(null);

      return;
    }

    if (!/^\d+$/.test(inputValue)) {
      return;
    }

    const numValue = Number.parseInt(inputValue, 10);

    if (Number.isNaN(numValue)) {
      return;
    }

    if (numValue > max) {
      onChange(max);

      return;
    }

    onChange(numValue);
  };

  const onSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange?.(
      isCompactView ? !event.target.checked : event.target.checked,
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent decimal point, minus sign, plus sign, 'e' (exponential)
    if (
      event.key === "." ||
      event.key === "-" ||
      event.key === "+" ||
      event.key === "e" ||
      event.key === "E"
    ) {
      event.preventDefault();
    }
  };

  const hasError =
    effectiveMin !== null &&
    !checked &&
    (value === null || value < effectiveMin);
  const showError = hasError;
  const errorMessage = hasError
    ? t("components.seatPicker.minimumValue", { value: effectiveMin })
    : "";

  const isCompactView = useIsMobile() || showCompactView;

  const displayedCheckedState = isCompactView ? !checked : checked;

  const labelText = isCompactView
    ? mobileLabel
    : t("components.seatPicker.unlimited");

  return (
    <Stack spacing={1} sx={{ mt: "24px" }}>
      {label && !isCompactView && (
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: theme.colors.text[1],
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: isCompactView ? "column" : "row",
          alignItems: isCompactView ? "flex-start" : "center",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={displayedCheckedState}
                onChange={onSwitchChange}
              />
            }
            label={labelText}
            labelPlacement={isCompactView ? "start" : "end"}
            sx={{
              marginLeft: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: 12,
                fontWeight: 500,
                color: theme.colors.text[displayedCheckedState ? 7 : 0],
                width: isCompactView ? "auto" : labelWidth,
                ml: isCompactView ? 0 : 1,
                mr: isCompactView ? 1 : 0,
              },
            }}
          />
          {showTooltip && (
            <Tooltip
              message={t("components.seatPicker.tooltip")}
              placement="right"
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.colors.secondary[6],
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
        </Box>
        <TextField
          disabled={checked}
          error={showError}
          helperText={errorMessage}
          inputProps={{
            min,
            max,
            type: "text",
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength,
          }}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Define a Limit"
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              bgcolor: theme.colors.grey,
              borderRadius: "16px",
              height: 48,
              border: "none",
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
              "&.Mui-disabled": {
                border: "none",
              },
              "&.Mui-error": {
                border: `1px solid ${theme.colors.red}`,
              },
            },
            "& .MuiInputBase-input": {
              color: theme.colors.text[6],
              fontWeight: 500,
              px: "18px",
              "&::placeholder": {
                color: theme.colors.text[6],
                fontSize: 14,
                opacity: 1,
              },
            },
            "& .MuiFormHelperText-root": {
              position: "absolute",
              bottom: "-22px",
              left: "12px",
              right: "12px",
              margin: 0,
              fontSize: "11px",
              padding: "2px 6px",
              borderRadius: "4px",
              color: theme.colors.red,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              zIndex: 1,
            },
          }}
          value={checked ? "∞" : (value ?? "")}
          variant="outlined"
        />
      </Box>
    </Stack>
  );
};
